import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  useGetBeefFeedAdditives,
  useGetFootprintingBeefFeedAdditive,
  useGetFootprintingBeefPrimaryDataById,
  useKeyedFormList,
  useMutateFootprintingBeefFeedAdditive,
  useMutateFootprintingBeefPrimaryData,
  useTranslation,
} from "utils";

import {
  ENUM_BEEF_FEED_ADDITIVE_UNITS,
  ENUM_FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE,
  FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE,
  UploadableBeefFeedAdditive,
} from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Typography,
} from "components";

type BeefFeedAdditiveFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const feedAdditivePlaceholder: UploadableBeefFeedAdditive = {
  id: undefined,
  feedAdditiveId: "",
  amount: null,
  amountUnit: null,
};

const BeefFeedAdditiveForm = ({ primaryDataId, readOnly, farmId, year }: BeefFeedAdditiveFormProps) => {
  const { t } = useTranslation("footprintingBeef");
  const formRef = useRef<FormInstance>(null);

  const [feedAdditiveActive, setFeedAdditiveActive] = useState<ENUM_FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE | null>(null);

  const { data: beefPrimaryData, isLoading } = useGetFootprintingBeefPrimaryDataById(primaryDataId ?? "");
  const keyedFeedAdditives = useKeyedFormList<UploadableBeefFeedAdditive>(formRef, { isReady: !isLoading });

  const feedAdditivesPayloadRef = useRef(keyedFeedAdditives.payload);

  useEffect(() => {
    feedAdditivesPayloadRef.current = keyedFeedAdditives.payload;
  }, [keyedFeedAdditives.payload]);
  const { data: feedAdditivesQuery } = useGetFootprintingBeefFeedAdditive(primaryDataId ?? "");
  const { data: beefFeedAdditivesQuery } = useGetBeefFeedAdditives();

  const selectGroupName = "footprinting-beef-feed-additive-select";

  const { mutate: mutateBeefFeedAdditives } = useMutateFootprintingBeefFeedAdditive(primaryDataId ?? "", farmId, year);
  const { mutate: mutateBeefPrimaryData } = useMutateFootprintingBeefPrimaryData(farmId, year, { farmId, year, feedAdditiveActive }, primaryDataId);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateBeefPrimaryData({});
        mutateBeefFeedAdditives(feedAdditivesPayloadRef.current.filter((a) => !!a.feedAdditiveId));
      }
    },
    [isLoading, mutateBeefPrimaryData, mutateBeefFeedAdditives, readOnly]
  );

  useEffect(() => {
    if (feedAdditivesQuery) {
      keyedFeedAdditives.actions.hydrate(
        feedAdditivesQuery.length
          ? feedAdditivesQuery.map((a) => ({
              id: a.id,
              feedAdditiveId: a.feedAdditiveId ?? "",
              amount: a.amount ?? null,
              amountUnit: a.amountUnit ?? null,
            }))
          : [feedAdditivePlaceholder]
      );
    }
  }, [feedAdditivesQuery, keyedFeedAdditives.actions]);

  useEffect(() => {
    if (beefPrimaryData) {
      setFeedAdditiveActive((beefPrimaryData.feedAdditiveActive as ENUM_FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE) ?? null);

      formRef.current?.setFieldsValue({
        feedAdditiveActive: beefPrimaryData.feedAdditiveActive ?? null,
      });

      if (beefPrimaryData.feedAdditiveActive != null) {
        keyedFeedAdditives.actions.requestRehydrationValidation();
      }
    }
  }, [beefPrimaryData, keyedFeedAdditives.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-beef-feed-additives-form" className="padding-100">
      <Form ref={formRef} preserve={false}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <Typography variant="paragraph-m" style={{ maxWidth: "64%" }}>
              {t("beefFeedAdditiveForm.feedAdditivesQuestion")}
              <span className="required-asterisk">*</span>
            </Typography>
            <KlimRadioGroup
              required
              name="feedAdditiveActive"
              dataTestId="footprinting-beef-feed-additives-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setFeedAdditiveActive(target.value);
                if (target.value === FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE.YES && keyedFeedAdditives.entries.length === 0) {
                  keyedFeedAdditives.actions.add(feedAdditivePlaceholder);
                }
              }}
              value={feedAdditiveActive}
              defaultValue={feedAdditiveActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE.YES, FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE.NO].map((option) => ({
                label: t(`beefFeedAdditiveForm.feedAdditiveOptions.${option}`),
                value: option,
              }))}
            />
          </Flex>
          {feedAdditiveActive === FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE.YES && (
            <Flex vertical gap={16}>
              {keyedFeedAdditives.entries.map((additive, index) => {
                const selectGroupWithIndex = `${selectGroupName}-${index}`;

                return (
                  <Form.Item key={additive.clientKey} className="padding-left-100 padding-right-100">
                    <Flex vertical gap={16}>
                      <Flex justify="space-between" align="center" vertical={false}>
                        <Typography variant="h5" style={{ flex: 1 }}>
                          {t("beefFeedAdditiveForm.additiveNumber", { count: index + 1 })}
                        </Typography>
                        <IconButton
                          type="tertiary"
                          disabled={readOnly}
                          dataTestId={`delete-beef-feed-additive-${index}`}
                          size="small"
                          iconName="tabler/trash"
                          onClick={() => {
                            keyedFeedAdditives.actions.remove(additive.clientKey);
                            if (keyedFeedAdditives.entries.length === 1) setFeedAdditiveActive(null);
                          }}
                        />
                      </Flex>
                      <Select
                        disabled={readOnly}
                        name={`${selectGroupName}-${additive.clientKey}`}
                        dataTestId={selectGroupWithIndex}
                        label={t("beefFeedAdditiveForm.question.feedAdditiveTypeQuestionInput")}
                        rules={[{ required: true, message: "" }]}
                        initialValue={beefFeedAdditivesQuery?.find((a) => a.id === additive.feedAdditiveId)?.name || ""}
                        value={beefFeedAdditivesQuery?.find((a) => a.id === additive.feedAdditiveId)?.name || ""}
                        onChange={(v) => {
                          const matchingAdditive = beefFeedAdditivesQuery?.find((a) => a.name === v);
                          keyedFeedAdditives.actions.update(additive.clientKey, { feedAdditiveId: matchingAdditive ? matchingAdditive.id : "" });
                        }}
                        dropdownStyle={{ zIndex: 10010 }}
                      >
                        {(beefFeedAdditivesQuery ?? []).map((beefFeedAdditive) => {
                          const isAlreadyUsed = keyedFeedAdditives.entries.some(
                            (a) => a.feedAdditiveId === beefFeedAdditive.id && a.feedAdditiveId !== additive.feedAdditiveId
                          );
                          return (
                            <Option
                              disabled={readOnly || isAlreadyUsed}
                              key={`${selectGroupWithIndex}-${beefFeedAdditive.id}`}
                              data-testid={`${selectGroupWithIndex}-${beefFeedAdditive.id}`}
                              value={beefFeedAdditive.name}
                            >
                              {beefFeedAdditive.name}
                            </Option>
                          );
                        })}
                      </Select>

                      <InputNumberWithUnitSelector
                        key={`amount-${additive.clientKey}-${additive.feedAdditiveId}`}
                        className="floating-label"
                        disabled={readOnly}
                        dataTestId={`footprinting-beef-feed-additive-amount-input-${index}`}
                        name={`amount-${additive.clientKey}`}
                        label={t("beefFeedAdditiveForm.question.amountUsedQuestionInput")}
                        unitLabel={t("unit", { ns: "footprinting" })}
                        units={["kilograms", "liters"]}
                        unitDisplayOverride={{
                          kilograms: "kg",
                          liters: "l",
                        }}
                        value={additive.amount}
                        initialValue={additive.amount}
                        unitValue={additive.amountUnit}
                        onChange={(v, unit) =>
                          keyedFeedAdditives.actions.update(additive.clientKey, { amount: v, amountUnit: unit as ENUM_BEEF_FEED_ADDITIVE_UNITS })
                        }
                        validateFirst
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("beefFeedAdditiveForm.error.amountUsedError")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Flex>
                  </Form.Item>
                );
              })}
            </Flex>
          )}
          {feedAdditiveActive === FARM_FOOTPRINTING_BEEF_FEED_ADDITIVE_ACTIVE.YES && (
            <Button
              type="text"
              dataTestId="add-beef-feed-additive-button"
              disabled={readOnly}
              onClick={() => keyedFeedAdditives.actions.add(feedAdditivePlaceholder)}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("beefFeedAdditiveForm.add")}
            </Button>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

BeefFeedAdditiveForm.Key = "beef.feedAdditives";
export { BeefFeedAdditiveForm };
